import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';

// Async thunks
export const fetchProjectInvoices = createAsyncThunk(
  'financial/fetchProjectInvoices',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/quickbooks/projects/${projectId}/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 && data.authRequired) {
          return rejectWithValue({
            message: data.message || 'QuickBooks authentication required',
            authRequired: true,
            authUrl: data.authUrl
          });
        }
        return rejectWithValue({
          message: data.message || 'Failed to fetch project invoices',
          authRequired: false
        });
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch project invoices',
        authRequired: false
      });
    }
  }
);

export const fetchProjectFinancialOverview = createAsyncThunk(
  'financial/fetchProjectFinancialOverview',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/quickbooks/projects/${projectId}/financial-overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Check if this is an authentication error
        if (response.status === 401 && data.authRequired) {
          return rejectWithValue({
            message: data.message || 'QuickBooks authentication required',
            authRequired: true,
            authUrl: data.authUrl
          });
        }
        return rejectWithValue({
          message: data.message || 'Failed to fetch financial overview',
          authRequired: false
        });
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch financial overview',
        authRequired: false
      });
    }
  }
);

export const fetchProjectTransactions = createAsyncThunk(
  'financial/fetchProjectTransactions',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/quickbooks/projects/${projectId}/transactions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 && data.authRequired) {
          return rejectWithValue({
            message: data.message || 'QuickBooks authentication required',
            authRequired: true,
            authUrl: data.authUrl
          });
        }
        return rejectWithValue({
          message: data.message || 'Failed to fetch project transactions',
          authRequired: false
        });
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch project transactions',
        authRequired: false
      });
    }
  }
);

export const fetchProjectLaborCosts = createAsyncThunk(
  'financial/fetchProjectLaborCosts',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/quickbooks/projects/${projectId}/labor-costs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 && data.authRequired) {
          return rejectWithValue({
            message: data.message || 'QuickBooks authentication required',
            authRequired: true,
            authUrl: data.authUrl
          });
        }
        return rejectWithValue({
          message: data.message || 'Failed to fetch labor costs',
          authRequired: false
        });
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch labor costs',
        authRequired: false
      });
    }
  }
);

export const addLaborEntry = createAsyncThunk(
  'financial/addLaborEntry',
  async ({ projectId, laborEntry }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/quickbooks/projects/${projectId}/labor-entries`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(laborEntry)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to add labor entry');
      }
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add labor entry');
    }
  }
);

export const fetchOpenBalanceInvoices = createAsyncThunk(
  'financial/fetchOpenBalanceInvoices',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/quickbooks/projects/${projectId}/open-balance-invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 && data.authRequired) {
          return rejectWithValue({
            message: data.message || 'QuickBooks authentication required',
            authRequired: true,
            authUrl: data.authUrl
          });
        }
        return rejectWithValue({
          message: data.message || 'Failed to fetch open balance invoices',
          authRequired: false
        });
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch open balance invoices',
        authRequired: false
      });
    }
  }
);

// Disconnect from QuickBooks
export const disconnectQuickbooks = createAsyncThunk(
  'financial/disconnectQuickbooks',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/quickbooks/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue({
          message: data.message || 'Failed to disconnect from QuickBooks'
        });
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to disconnect from QuickBooks'
      });
    }
  }
);

// Initial state
const initialState = {
  invoices: [],
  financialOverview: null,
  transactions: [],
  laborCosts: [],
  openBalanceInvoices: [],
  allInvoices: [],
  estimates: [],
  loading: false,
  disconnecting: false,
  error: null,
  activeFinancialTab: 'overview',
  authRequired: false,
  authUrl: null
};

// Slice
const financialSlice = createSlice({
  name: 'financial',
  initialState,
  reducers: {
    setActiveFinancialTab: (state, action) => {
      state.activeFinancialTab = action.payload;
    },
    resetFinancialState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // fetchProjectInvoices
      .addCase(fetchProjectInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.authRequired = false;
      })
      .addCase(fetchProjectInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices || [];
        state.authRequired = false;
      })
      .addCase(fetchProjectInvoices.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.authRequired) {
          state.authRequired = true;
          state.authUrl = action.payload.authUrl;
          state.error = 'QuickBooks authentication required';
        } else {
          state.authRequired = false;
          state.error = action.payload?.message || action.payload || 'Failed to fetch invoices';
        }
      })
      
      // fetchProjectFinancialOverview
      .addCase(fetchProjectFinancialOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.authRequired = false;
      })
      .addCase(fetchProjectFinancialOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.financialOverview = action.payload.financialOverview || null;
        state.authRequired = false;
      })
      .addCase(fetchProjectFinancialOverview.rejected, (state, action) => {
        state.loading = false;
        
        // Check if this is an authentication error
        if (action.payload?.authRequired) {
          state.authRequired = true;
          state.authUrl = action.payload.authUrl;
          state.error = 'QuickBooks authentication required';
        } else {
          state.authRequired = false;
          state.error = action.payload?.message || action.payload || 'Failed to fetch financial overview';
        }
      })
      
      // fetchProjectTransactions
      .addCase(fetchProjectTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.authRequired = false;
      })
      .addCase(fetchProjectTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions || [];
        state.authRequired = false;
      })
      .addCase(fetchProjectTransactions.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.authRequired) {
          state.authRequired = true;
          state.authUrl = action.payload.authUrl;
          state.error = 'QuickBooks authentication required';
        } else {
          state.authRequired = false;
          state.error = action.payload?.message || action.payload || 'Failed to fetch transactions';
        }
      })
      
      // fetchProjectLaborCosts
      .addCase(fetchProjectLaborCosts.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.authRequired = false;
      })
      .addCase(fetchProjectLaborCosts.fulfilled, (state, action) => {
        state.loading = false;
        state.laborCosts = action.payload.laborCosts || [];
        state.authRequired = false;
      })
      .addCase(fetchProjectLaborCosts.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.authRequired) {
          state.authRequired = true;
          state.authUrl = action.payload.authUrl;
          state.error = 'QuickBooks authentication required';
        } else {
          state.authRequired = false;
          state.error = action.payload?.message || action.payload || 'Failed to fetch labor costs';
        }
      })
      
      // addLaborEntry
      .addCase(addLaborEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addLaborEntry.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.laborEntry) {
          state.laborCosts.push(action.payload.laborEntry);
        }
      })
      .addCase(addLaborEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to add labor entry';
      })
      
      // fetchOpenBalanceInvoices (now returns all invoices + estimates)
      .addCase(fetchOpenBalanceInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.authRequired = false;
      })
      .addCase(fetchOpenBalanceInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.openBalanceInvoices = action.payload.openBalanceInvoices || [];
        state.allInvoices = action.payload.allInvoices || action.payload.openBalanceInvoices || [];
        state.estimates = action.payload.estimates || [];
        state.authRequired = false;
      })
      .addCase(fetchOpenBalanceInvoices.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.authRequired) {
          state.authRequired = true;
          state.authUrl = action.payload.authUrl;
          state.error = 'QuickBooks authentication required';
        } else {
          state.authRequired = false;
          state.error = action.payload?.message || action.payload || 'Failed to fetch invoices';
        }
      })
      
      // disconnectQuickbooks
      .addCase(disconnectQuickbooks.pending, (state) => {
        state.disconnecting = true;
        state.error = null;
      })
      .addCase(disconnectQuickbooks.fulfilled, (state) => {
        state.disconnecting = false;
        state.authRequired = true;
        state.authUrl = null;
        // Clear all financial data
        state.invoices = [];
        state.financialOverview = null;
        state.transactions = [];
        state.laborCosts = [];
        state.openBalanceInvoices = [];
        state.allInvoices = [];
        state.estimates = [];
      })
      .addCase(disconnectQuickbooks.rejected, (state, action) => {
        state.disconnecting = false;
        state.error = action.payload?.message || 'Failed to disconnect from QuickBooks';
      });
  }
});

export const { setActiveFinancialTab, resetFinancialState } = financialSlice.actions;

export default financialSlice.reducer;
